import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/sports';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'bb体育',
    },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bbsports.com/' },
      { '@type': 'ListItem', position: 2, name: `${article.category}资讯`, item: `https://www.bbsports.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.bbsports.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <header>
        <Navbar />
      </header>

      <main>
        <article className="w-full pt-24 pb-14 md:pb-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span className="text-foreground-300">/</span>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer">{article.category}资讯</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-700">{article.title}</span>
            </nav>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-label font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500">·</span>
                <span className="text-xs text-foreground-500">{article.readTime} 阅读</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 pb-6 border-b border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-label font-medium text-foreground-950">{article.author}</p>
                  <p className="text-xs text-foreground-500">bb体育官方分析师</p>
                </div>
              </div>
            </div>

            <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              {article.content.split('\n\n').filter(Boolean).map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-foreground-500 mb-1">标签</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">{article.category}</span>
                    <span className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">赛事分析</span>
                    <span className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">bb体育</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button className="w-9 h-9 rounded-lg border border-background-300 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-foreground-600 text-sm"></i>
                  </button>
                  <button className="w-9 h-9 rounded-lg border border-background-300 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-foreground-600 text-sm"></i>
                  </button>
                  <button className="w-9 h-9 rounded-lg border border-background-300 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-foreground-600 text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">相关文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {articles
                .filter((a) => a.id !== article.id && a.category === article.category)
                .slice(0, 2)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl p-4 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs font-label font-medium">
                        {related.category}
                      </span>
                      <span className="text-xs text-foreground-500">{related.date}</span>
                    </div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {related.title}
                    </h4>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}